import React, { useState, useEffect } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Link } from "react-router-dom";
import axios from "axios";
import config from "../config";
import Faq from "./Faq";
import service1 from "../image/gym-setup/gym-setup-work-by-meragym-v1.jpg";
import service2 from "../image/gym-setup/gym-design-by-meragym-v1.jpg";
import service3 from "../image/gym-setup/gym-equipment-installation-v1.jpg";
import service4 from "../image/gym-setup/gym-interior-design-india-v1.jpg";
import gymSetupHero from "../image/gym-setup/gym-setup-hero-v1.webp";
import servicePlanningImage from "../image/gym-setup/service-planning-v1.webp";
import serviceInstallationImage from "../image/gym-setup/service-installation-v1.webp";
import serviceBrandingImage from "../image/gym-setup/service-branding-v1.webp";
import serviceFlooringImage from "../image/gym-setup/service-flooring-v1.webp";
import serviceAmbienceImage from "../image/gym-setup/service-ambience-v1.webp";
import serviceRenovationImage from "../image/gym-setup/service-renovation-v1.webp";
import galleryStrengthFloor from "../image/gym-setup/gallery-strength-floor-v1.webp";
import galleryFunctionalZone from "../image/gym-setup/gallery-functional-zone-v1.webp";
import galleryReception from "../image/gym-setup/gallery-reception-v1.webp";

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      "@id": "https://meragym.com/gym-setup-and-design-work#service",
      name: "Gym Setup & Design Work",
      description:
        "Professional gym setup and interior design services in India. Equipment procurement, space planning, flooring, branding, lighting and complete turnkey gym solutions.",
      provider: {
        "@type": "Organization",
        name: "MeraGym",
        url: "https://meragym.com",
      },
      areaServed: "IN",
      serviceType: "Gym Setup and Design",
      url: "https://meragym.com/gym-setup-and-design-work",
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://meragym.com" },
        { "@type": "ListItem", position: 2, name: "Gym Setup & Design Work", item: "https://meragym.com/gym-setup-and-design-work" },
      ],
    },
  ],
};

const services = [
  {
    img: servicePlanningImage,
    icon: "fas fa-comments",
    title: "Consultation & Planning",
    desc: "We understand your vision, space, and budget to craft a tailored gym blueprint that maximises functionality and wow-factor.",
  },
  {
    img: serviceInstallationImage,
    icon: "fas fa-dumbbell",
    title: "Equipment Procurement & Installation",
    desc: "Cardio, strength, functional training — we source, deliver, and professionally install every piece of equipment.",
  },
  {
    img: serviceBrandingImage,
    icon: "fas fa-award",
    title: "Branding & Signage",
    desc: "Logo placement, wall graphics, and branded equipment covers that turn your gym into a powerful marketing asset.",
  },
  {
    img: serviceFlooringImage,
    icon: "fas fa-layer-group",
    title: "Flooring & Locker Rooms",
    desc: "Rubber flooring, turf zones, anti-slip tiles, and fully fitted locker/changing rooms designed for durability and hygiene.",
  },
  {
    img: serviceAmbienceImage,
    icon: "fas fa-lightbulb",
    title: "Lighting & Sound Ambiance",
    desc: "Professional lighting design and sound systems that energise members, reduce fatigue, and elevate the overall experience.",
  },
  {
    img: serviceRenovationImage,
    icon: "fas fa-ruler-combined",
    title: "Interior Design & Renovation",
    desc: "Full interior overhaul — walls, ceilings, mirrors, colour schemes — so your gym looks as elite as it performs.",
  },
];

const steps = [
  { num: "01", title: "Free Consultation", desc: "Share your space details, goals, and budget. Our expert visits the site or connects virtually." },
  { num: "02", title: "Design & Quotation", desc: "We prepare a detailed 3D layout, equipment list, and transparent cost breakdown." },
  { num: "03", title: "Procurement & Execution", desc: "Equipment is sourced, customised if needed, and the entire setup is executed on schedule." },
  { num: "04", title: "Handover & Support", desc: "We hand over a ready-to-run gym and provide training, warranty, and ongoing maintenance support." },
];

const whyCards = [
  { icon: "fas fa-tools", title: "End-to-End Turnkey", desc: "One partner for planning, procurement, installation, and after-sales — zero hassle." },
  { icon: "fas fa-rupee-sign", title: "Transparent Pricing", desc: "Detailed quotes with no hidden costs. We work within your budget without cutting corners." },
  { icon: "fas fa-clock", title: "On-Time Delivery", desc: "We respect your launch date. Our project managers ensure every milestone is met." },
  { icon: "fas fa-medal", title: "Premium Equipment", desc: "We partner with trusted brands to deliver gym-grade equipment built to last." },
  { icon: "fas fa-palette", title: "Custom Branding", desc: "Your logo, your colours, your identity — embedded into every corner of your gym." },
  { icon: "fas fa-headset", title: "Post-Setup Support", desc: "Dedicated support team for maintenance, repairs, and future expansion needs." },
];

export default function GymSetup() {
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    axios
      .get(`${config.apiUrl}/page-video?page=gym_setup_and_design`)
      .then((res) => {
        if (res.data.success && res.data.data) {
          setVideos(Array.isArray(res.data.data) ? res.data.data : [res.data.data]);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Gym Setup and Design Work | Complete Gym Setup Services — MeraGym</title>
          <meta
            name="description"
            content="Professional gym setup and design work across India. Equipment procurement, space planning, flooring, branding & lighting. Get a free consultation for your new gym setup today."
          />
          <meta
            name="keywords"
            content="gym setup and design work, gym setup services India, gym interior design, gym equipment installation, new gym setup, gym setup company, gym design consultant"
          />
          <link rel="canonical" href="https://meragym.com/gym-setup-and-design-work" />
          <meta property="og:title" content="Gym Setup and Design Work | MeraGym" />
          <meta property="og:description" content="End-to-end gym setup and design services — equipment, flooring, branding, lighting & more. Free consultation across India." />
          <meta property="og:url" content="https://meragym.com/gym-setup-and-design-work" />
          <meta property="og:type" content="website" />
          <meta property="og:image" content="https://meragym.com/og-gym-setup.jpg" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Gym Setup and Design Work | MeraGym" />
          <meta name="twitter:description" content="End-to-end gym setup and design services across India." />
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        </Helmet>
      </HelmetProvider>

      {/* ── Hero ── */}
      <section className="gsd-hero" style={{ backgroundImage: `url(${gymSetupHero})` }}>
        <div className="gsd-hero-overlay">
          <div className="container">
            <nav className="gsd-breadcrumb" aria-label="breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>Gym Setup &amp; Design Work</span>
            </nav>
            <span className="gsd-hero-kicker"><i className="fas fa-drafting-compass"></i> Turnkey gym setup specialists</span>
            <h1 className="gsd-hero-title">
              Professional Gym Setup &amp; Design Work<br />
              <span>Across India</span>
            </h1>
            <p className="gsd-hero-sub">
              From consultation and equipment to flooring, branding, and lighting — we deliver turnkey gym spaces that inspire members and drive results.
            </p>
            <div className="gsd-hero-actions">
              <a href="tel:+919889415543" className="gsd-btn-primary">
                <i className="fas fa-phone-alt me-2"></i>Get Free Consultation
              </a>
              <a href="#gsd-services" className="gsd-btn-outline">Explore Services</a>
            </div>
            <div className="gsd-hero-assurance">
              <span><i className="fas fa-check"></i> Space planning to opening day</span>
              <span><i className="fas fa-check"></i> One accountable setup partner</span>
            </div>
            <div className="gsd-hero-stats">
              <div className="gsd-stat"><strong>500+</strong><span>Gyms Setup</span></div>
              <div className="gsd-stat"><strong>10+</strong><span>States Covered</span></div>
              <div className="gsd-stat"><strong>5+ Yrs</strong><span>Experience</span></div>
              <div className="gsd-stat"><strong>100%</strong><span>Turnkey</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Intro ── */}
      <section className="gsd-intro-section">
        <div className="container">
          <div className="row align-items-center gy-4">
            <div className="col-lg-6">
              <span className="gsd-badge">Gym Setup Experts</span>
              <h2 className="gsd-section-title mt-2">
                We Build Gyms That<br /><span>Members Love Coming Back To</span>
              </h2>
              <p className="gsd-intro-text">
                MeraGym's gym setup and design team has helped over 500 gym owners across India open their dream fitness centres. Whether you're starting fresh or renovating an existing space, we manage the entire project from blueprints to opening day.
              </p>
              <p className="gsd-intro-text">
                Our experts handle space planning, premium equipment sourcing, custom branding, flooring, lighting, and every detail in between — all under one roof, on budget and on time.
              </p>
              <a href="tel:+919889415543" className="gsd-btn-primary mt-2">
                Talk to Our Setup Expert
              </a>
            </div>
            <div className="col-lg-6">
              <div className="gsd-intro-grid">
                <img src={service1} alt="Gym setup work by MeraGym" className="img-fluid" />
                <img src={service2} alt="Gym design by MeraGym" className="img-fluid" />
                <img src={service3} alt="Gym equipment installation" className="img-fluid" />
                <img src={service4} alt="Gym interior design India" className="img-fluid" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Services ── */}
      <section className="gsd-services-section" id="gsd-services">
        <div className="container">
          <div className="gsd-section-header">
            <span className="gsd-badge">What We Do</span>
            <h2 className="gsd-section-title mt-2">Complete Gym Setup &amp; Design Services</h2>
            <p className="gsd-section-sub">Everything you need to launch a world-class gym — handled by our specialists.</p>
          </div>
          <div className="row gy-4">
            {services.map((s, i) => (
              <div className="col-lg-4 col-md-6" key={i}>
                <div className="gsd-service-card">
                  <div className="gsd-service-img">
                    <img src={s.img} alt={s.title} />
                    <div className="gsd-service-icon"><i className={s.icon}></i></div>
                  </div>
                  <div className="gsd-service-body">
                    <h3>{s.title}</h3>
                    <p>{s.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Gallery ── */}
      <section className="gsd-gallery-section">
        <div className="container">
          <div className="gsd-section-header">
            <span className="gsd-badge">Our Work</span>
            <h2 className="gsd-section-title mt-2">Gym Setup &amp; Design Work Gallery</h2>
            <p className="gsd-section-sub">A glimpse of the professional gym spaces we have built across India.</p>
          </div>
          <div className="gsd-gallery-grid">
            <div className="gsd-gallery-item gsd-gallery-large"><img src={galleryStrengthFloor} alt="Completed strength training floor" /><span className="gsd-gallery-caption">Strength floor</span></div>
            <div className="gsd-gallery-item"><img src={galleryFunctionalZone} alt="Completed functional training zone" /><span className="gsd-gallery-caption">Functional training</span></div>
            <div className="gsd-gallery-item"><img src={galleryReception} alt="Completed gym reception interior" /><span className="gsd-gallery-caption">Arrival experience</span></div>
            <div className="gsd-gallery-item"><img src={serviceFlooringImage} alt="Gym turf and flooring detail" /><span className="gsd-gallery-caption">Flooring details</span></div>
            <div className="gsd-gallery-item gsd-gallery-large"><img src={serviceRenovationImage} alt="Completed premium gym interior" /><span className="gsd-gallery-caption">Complete interiors</span></div>
            <div className="gsd-gallery-item"><img src={serviceBrandingImage} alt="Gym wall branding detail" /><span className="gsd-gallery-caption">Brand accents</span></div>
          </div>
        </div>
      </section>

      {/* ── How We Work ── */}
      <section className="gsd-process-section">
        <div className="container">
          <div className="gsd-section-header">
            <span className="gsd-badge">Our Process</span>
            <h2 className="gsd-section-title mt-2">How Our Gym Setup Process Works</h2>
            <p className="gsd-section-sub">A simple, transparent 4-step journey from idea to grand opening.</p>
          </div>
          <div className="gsd-process-grid">
            {steps.map((s, i) => (
              <div className="gsd-process-step" key={i}>
                <div className="gsd-process-num">{s.num}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                {i < steps.length - 1 && <div className="gsd-process-arrow"><i className="fas fa-arrow-right"></i></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Admin Video ── */}
      {videos.length > 0 && (
        <section className="gsd-video-section">
          <div className="container">
            <div className="gsd-section-header">
              <span className="gsd-badge">Watch Us Work</span>
              <h2 className="gsd-section-title mt-2">Gym Setup &amp; Design Work Videos</h2>
              <p className="gsd-section-sub">See how we transform raw spaces into premium gym environments.</p>
            </div>
            <div className={`gsd-video-grid${videos.length === 1 ? " gsd-video-grid--single" : ""}`}>
              {videos.map((v, i) => (
                <div className="gsd-video-item" key={i}>
                  <div className="gsd-video-wrapper">
                    <iframe
                      src={v.embed_url}
                      title={v.title || `Gym Setup Video ${i + 1}`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                  {(v.title || v.description) && (
                    <div className="gsd-video-meta">
                      {v.title && <strong>{v.title}</strong>}
                      {v.description && <span>{v.description}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Why Choose Us ── */}
      <section className="gsd-why-section">
        <div className="container">
          <div className="row gsd-why-layout align-items-center">
            <div className="col-lg-5">
              <div className="gsd-why-intro">
                <span className="gsd-badge">Why MeraGym</span>
                <h2>One team. One plan.<br /><span>A gym built to open strong.</span></h2>
                <p>We bring the moving parts of a gym launch under one accountable setup partner, so you can focus on the business ahead.</p>
                <div className="gsd-why-promise">
                  <i className="fas fa-shield-alt"></i>
                  <div><strong>Clear ownership from day one</strong><span>Planning, procurement, execution, and post-launch support stay connected.</span></div>
                </div>
                <div className="gsd-why-proof">
                  <div><strong>500+</strong><span>projects supported</span></div>
                  <div><strong>10+</strong><span>states covered</span></div>
                  <div><strong>5+ yrs</strong><span>specialist experience</span></div>
                </div>
                <a href="tel:+919889415543" className="gsd-why-link">Talk to a setup expert <i className="fas fa-arrow-right"></i></a>
              </div>
            </div>
            <div className="col-lg-7">
              <div className="gsd-why-grid">
                {whyCards.map((c) => (
                  <article className="gsd-why-card" key={c.title}>
                    <div className="gsd-why-icon"><i className={c.icon}></i></div>
                    <div><h3>{c.title}</h3><p>{c.desc}</p></div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="gsd-faq-section">
        <div className="container">
          <div className="gsd-section-header">
            <span className="gsd-badge">FAQs</span>
            <h2 className="gsd-section-title mt-2">Gym Setup &amp; Design — Frequently Asked Questions</h2>
          </div>
          <div className="row">
            <div className="col-lg-9 mx-auto">
              <Faq page="gym_setup_and_design" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="gsd-cta-section">
        <div className="container">
          <div className="gsd-cta-card">
            <h2>Ready to Build Your Dream Gym?</h2>
            <p>Talk to our gym setup experts today. Free consultation, transparent pricing, on-time delivery — guaranteed.</p>
            <div className="gsd-cta-actions">
              <a href="tel:+919889415543" className="gsd-btn-primary">
                <i className="fas fa-phone-alt me-2"></i>Call Us Now
              </a>
              <Link to="/contact" className="gsd-btn-outline-white">Send an Enquiry</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
